import { useCallback, useEffect, useState } from 'react'
import useInterval from './use-interval'
import useDidMountEffect from './use-did-mount-effect'
import { debounce } from '@mui/material'

const isVisible = () => document.visibilityState === 'visible'
const visibilityDebounceMs = 1000

const usePolling = (cb, ms) => {
  const [isDocumentVisible, setIsDocumentVisible] = useState(isVisible())
  const onVisibilityChange = useCallback(() => setIsDocumentVisible(isVisible()), [])
  // eslint-disable-next-line
  const debouncedOnVisibilityChange = useCallback(debounce(onVisibilityChange, visibilityDebounceMs), [])

  useInterval(cb, ms, !isDocumentVisible)

  useEffect(() => {
    const handleVisible = () => {
      if (isVisible()) {
        debouncedOnVisibilityChange()
      } else {
        debouncedOnVisibilityChange.clear()
        setIsDocumentVisible(false)
      }
    }

    // pageshow covers bfcache restores on iOS Safari (e.g. returning from SMS link)
    const handlePageShow = () => {
      debouncedOnVisibilityChange()
    }

    // focus covers Android WebView / Chrome tab switching
    const handleFocus = () => {
      debouncedOnVisibilityChange()
    }

    window.addEventListener('visibilitychange', handleVisible)
    window.addEventListener('pageshow', handlePageShow)
    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('visibilitychange', handleVisible)
      window.removeEventListener('pageshow', handlePageShow)
      window.removeEventListener('focus', handleFocus)
    }
  }, [debouncedOnVisibilityChange, onVisibilityChange])

  useDidMountEffect(() => {
    if (isDocumentVisible) cb()
  }, [isDocumentVisible])
}

export default usePolling
